'use client';

import React, { useState, useMemo } from 'react';
import {
  ShoppingCart, Package, CheckCircle, Clock, AlertTriangle, ChevronRight,
  Search, Filter, ArrowRight, Truck, RotateCcw, X, Plus, Eye, Zap, BookOpen
} from 'lucide-react';
import Link from 'next/link';
import { useRole } from '@/lib/useRole';
import { MOCK_EGG_INVENTORY, EggInventoryItem, EggStockMovement, MOCK_STOCK_MOVEMENTS } from '../../components/eggProductionData';

// ─── Types ────────────────────────────────────────────────────────────────────

type OrderStatus = 'pending' | 'reserved' | 'packing' | 'dispatched' | 'delivered' | 'cancelled';
type FulfillmentStep = 'production' | 'grading' | 'reserved' | 'packing' | 'dispatch' | 'delivered';

interface CustomerOrder {
  id: string;
  orderNumber: string;
  customer: string;
  customerType: 'wholesale' | 'retail' | 'institutional';
  orderDate: string;
  requiredDate: string;
  eggGrade: string;
  quantityEggs: number;
  quantityTrays: number;
  reservedEggs: number;
  reservedTrays: number;
  availableStock: number;
  status: OrderStatus;
  fulfillmentStep: FulfillmentStep;
  assignedStore: string;
  packedBy?: string;
  dispatchedBy?: string;
  notes?: string;
  totalValue: number;
  priority: 'high' | 'normal' | 'low';
}

interface StockReservation {
  id: string;
  orderId: string;
  orderNumber: string;
  eggGrade: string;
  store: string;
  reservedQty: number;
  reservedTrays: number;
  reservedAt: string;
  expiresAt: string;
  status: 'active' | 'fulfilled' | 'released';
}

// ─── Mock Data ────────────────────────────────────────────────────────────────

const MOCK_ORDERS: CustomerOrder[] = [
  {
    id: 'ord-001', orderNumber: 'ORD-2026-0145', customer: 'Kampala Supermarket Ltd',
    customerType: 'wholesale', orderDate: '2026-09-03', requiredDate: '2026-09-06',
    eggGrade: 'Grade A Large', quantityEggs: 3600, quantityTrays: 120,
    reservedEggs: 3600, reservedTrays: 120, availableStock: 4200,
    status: 'reserved', fulfillmentStep: 'reserved',
    assignedStore: 'Main Egg Store', notes: 'Weekly standing order',
    totalValue: 1800000, priority: 'high',
  },
  {
    id: 'ord-002', orderNumber: 'ORD-2026-0146', customer: 'Jinja Poultry Traders',
    customerType: 'wholesale', orderDate: '2026-09-04', requiredDate: '2026-09-07',
    eggGrade: 'Grade A Medium', quantityEggs: 1800, quantityTrays: 60,
    reservedEggs: 1800, reservedTrays: 60, availableStock: 2100,
    status: 'packing', fulfillmentStep: 'packing',
    assignedStore: 'Main Egg Store', packedBy: 'James Okello',
    totalValue: 810000, priority: 'high',
  },
  {
    id: 'ord-003', orderNumber: 'ORD-2026-0147', customer: 'Mukwano Bakeries',
    customerType: 'institutional', orderDate: '2026-09-04', requiredDate: '2026-09-08',
    eggGrade: 'Grade B', quantityEggs: 900, quantityTrays: 30,
    reservedEggs: 600, reservedTrays: 20, availableStock: 750,
    status: 'pending', fulfillmentStep: 'grading',
    assignedStore: 'Grading Room', notes: 'Partial stock available — awaiting grading',
    totalValue: 270000, priority: 'normal',
  },
  {
    id: 'ord-004', orderNumber: 'ORD-2026-0148', customer: 'St. Mary\'s Hospital',
    customerType: 'institutional', orderDate: '2026-09-04', requiredDate: '2026-09-05',
    eggGrade: 'Grade A Large', quantityEggs: 600, quantityTrays: 20,
    reservedEggs: 600, reservedTrays: 20, availableStock: 4200,
    status: 'dispatched', fulfillmentStep: 'dispatch',
    assignedStore: 'Dispatch Area', dispatchedBy: 'David Mugisha',
    totalValue: 300000, priority: 'high',
  },
  {
    id: 'ord-005', orderNumber: 'ORD-2026-0149', customer: 'Nakasero Market Vendor',
    customerType: 'retail', orderDate: '2026-09-05', requiredDate: '2026-09-05',
    eggGrade: 'Grade A Small', quantityEggs: 300, quantityTrays: 10,
    reservedEggs: 0, reservedTrays: 0, availableStock: 980,
    status: 'pending', fulfillmentStep: 'production',
    assignedStore: 'Main Egg Store',
    totalValue: 105000, priority: 'normal',
  },
  {
    id: 'ord-006', orderNumber: 'ORD-2026-0150', customer: 'Entebbe Hotel Group',
    customerType: 'institutional', orderDate: '2026-09-02', requiredDate: '2026-09-04',
    eggGrade: 'Grade A Large', quantityEggs: 1200, quantityTrays: 40,
    reservedEggs: 1200, reservedTrays: 40, availableStock: 4200,
    status: 'delivered', fulfillmentStep: 'delivered',
    assignedStore: 'Dispatch Area', dispatchedBy: 'Sarah Namukasa',
    totalValue: 600000, priority: 'normal',
  },
  {
    id: 'ord-007', orderNumber: 'ORD-2026-0151', customer: 'Owino Market Cooperative',
    customerType: 'wholesale', orderDate: '2026-09-05', requiredDate: '2026-09-09',
    eggGrade: 'Hatching Grade', quantityEggs: 2400, quantityTrays: 80,
    reservedEggs: 0, reservedTrays: 0, availableStock: 1200,
    status: 'pending', fulfillmentStep: 'production',
    assignedStore: 'Cold Store A', notes: 'Insufficient hatching eggs — production needed',
    totalValue: 1440000, priority: 'low',
  },
];

const MOCK_RESERVATIONS: StockReservation[] = [
  { id: 'res-001', orderId: 'ord-001', orderNumber: 'ORD-2026-0145', eggGrade: 'Grade A Large', store: 'Main Egg Store', reservedQty: 3600, reservedTrays: 120, reservedAt: '2026-09-03T10:00:00Z', expiresAt: '2026-09-06T23:59:00Z', status: 'active' },
  { id: 'res-002', orderId: 'ord-002', orderNumber: 'ORD-2026-0146', eggGrade: 'Grade A Medium', store: 'Main Egg Store', reservedQty: 1800, reservedTrays: 60, reservedAt: '2026-09-04T08:30:00Z', expiresAt: '2026-09-07T23:59:00Z', status: 'active' },
  { id: 'res-003', orderId: 'ord-004', orderNumber: 'ORD-2026-0148', eggGrade: 'Grade A Large', store: 'Dispatch Area', reservedQty: 600, reservedTrays: 20, reservedAt: '2026-09-04T14:00:00Z', expiresAt: '2026-09-05T23:59:00Z', status: 'fulfilled' },
  { id: 'res-004', orderId: 'ord-006', orderNumber: 'ORD-2026-0150', eggGrade: 'Grade A Large', store: 'Dispatch Area', reservedQty: 1200, reservedTrays: 40, reservedAt: '2026-09-02T09:00:00Z', expiresAt: '2026-09-04T23:59:00Z', status: 'fulfilled' },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

const STATUS_CONFIG: Record<OrderStatus, { label: string; bg: string; text: string }> = {
  pending:    { label: 'Pending',    bg: 'bg-amber-100',  text: 'text-amber-700' },
  reserved:   { label: 'Reserved',   bg: 'bg-blue-100',   text: 'text-blue-700' },
  packing:    { label: 'Packing',    bg: 'bg-purple-100', text: 'text-purple-700' },
  dispatched: { label: 'Dispatched', bg: 'bg-indigo-100', text: 'text-indigo-700' },
  delivered:  { label: 'Delivered',  bg: 'bg-green-100',  text: 'text-green-700' },
  cancelled:  { label: 'Cancelled',  bg: 'bg-red-100',    text: 'text-red-600' },
};

const PRIORITY_CONFIG: Record<string, { label: string; dot: string }> = {
  high:   { label: 'High',   dot: 'bg-red-500' },
  normal: { label: 'Normal', dot: 'bg-amber-400' },
  low:    { label: 'Low',    dot: 'bg-gray-400' },
};

const WORKFLOW_STEPS: { key: FulfillmentStep; label: string; icon: React.ElementType }[] = [
  { key: 'production', label: 'Production',  icon: Package },
  { key: 'grading',    label: 'Grading',     icon: Filter },
  { key: 'reserved',   label: 'Reserved',    icon: CheckCircle },
  { key: 'packing',    label: 'Packing',     icon: ShoppingCart },
  { key: 'dispatch',   label: 'Dispatch',    icon: Truck },
  { key: 'delivered',  label: 'Delivered',   icon: CheckCircle },
];

const STEP_ORDER: FulfillmentStep[] = ['production', 'grading', 'reserved', 'packing', 'dispatch', 'delivered'];

function stepIndex(step: FulfillmentStep) { return STEP_ORDER.indexOf(step); }

function StatusBadge({ status }: { status: OrderStatus }) {
  const cfg = STATUS_CONFIG[status];
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${cfg.bg} ${cfg.text}`}>{cfg.label}</span>;
}

function PriorityDot({ priority }: { priority: string }) {
  const cfg = PRIORITY_CONFIG[priority] ?? PRIORITY_CONFIG.normal;
  return (
    <span className="inline-flex items-center gap-1.5 text-xs text-gray-500">
      <span className={`w-2 h-2 rounded-full ${cfg.dot}`} />
      {cfg.label}
    </span>
  );
}

function FulfillmentStepper({ current }: { current: FulfillmentStep }) {
  const currentIdx = stepIndex(current);
  return (
    <div className="flex items-center gap-0">
      {WORKFLOW_STEPS.map((step, i) => {
        const done = i < currentIdx;
        const active = i === currentIdx;
        const StepIcon = step.icon;
        return (
          <React.Fragment key={step.key}>
            <div className="flex flex-col items-center">
              <div className={`w-7 h-7 rounded-full flex items-center justify-center border-2 transition-colors ${
                done   ? 'bg-green-500 border-green-500' : active ?'bg-green-600 border-green-600': 'bg-white border-gray-200'
              }`}>
                <StepIcon size={13} className={done || active ? 'text-white' : 'text-gray-300'} />
              </div>
              <span className={`text-xs mt-1 font-medium whitespace-nowrap ${active ? 'text-green-700' : done ? 'text-gray-500' : 'text-gray-300'}`}>
                {step.label}
              </span>
            </div>
            {i < WORKFLOW_STEPS.length - 1 && (
              <div className={`h-0.5 w-6 sm:w-8 mb-4 flex-shrink-0 ${i < currentIdx ? 'bg-green-400' : 'bg-gray-200'}`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

// ─── Order Detail Drawer ──────────────────────────────────────────────────────

function OrderDetailDrawer({ order, onClose, onAdvance }: { order: CustomerOrder; onClose: () => void; onAdvance: (id: string) => void }) {
  const stockCoverage = order.quantityEggs > 0 ? Math.min(100, Math.round((order.reservedEggs / order.quantityEggs) * 100)) : 0;
  const canAdvance = order.status !== 'delivered' && order.status !== 'cancelled';

  const nextAction: Record<OrderStatus, string> = {
    pending:    'Reserve Stock',
    reserved:   'Start Packing',
    packing:    'Mark Dispatched',
    dispatched: 'Confirm Delivery',
    delivered:  '',
    cancelled:  '',
  };

  return (
    <div className="fixed inset-0 z-40 flex justify-end">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative w-full max-w-md bg-white shadow-2xl flex flex-col h-full overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-gray-100 sticky top-0 bg-white z-10">
          <div>
            <p className="text-xs text-gray-400 font-medium">{order.orderNumber}</p>
            <h2 className="text-base font-bold text-gray-900">{order.customer}</h2>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100 transition-colors">
            <X size={18} className="text-gray-500" />
          </button>
        </div>

        <div className="p-5 space-y-5 flex-1">
          {/* Status & Priority */}
          <div className="flex items-center gap-3 flex-wrap">
            <StatusBadge status={order.status} />
            <PriorityDot priority={order.priority} />
            <span className="text-xs text-gray-400 ml-auto">Due: <span className="font-semibold text-gray-700">{order.requiredDate}</span></span>
          </div>

          {/* Fulfillment Stepper */}
          <div className="bg-gray-50 rounded-xl p-4 overflow-x-auto">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Fulfillment Progress</p>
            <FulfillmentStepper current={order.fulfillmentStep} />
          </div>

          {/* Order Details */}
          <div className="bg-white border border-gray-100 rounded-xl p-4 space-y-3">
            <p className="text-xs font-bold text-gray-700 uppercase tracking-wide">Order Details</p>
            <div className="grid grid-cols-2 gap-3">
              {[
                { label: 'Egg Grade', value: order.eggGrade },
                { label: 'Customer Type', value: order.customerType.charAt(0).toUpperCase() + order.customerType.slice(1) },
                { label: 'Ordered Qty', value: `${order.quantityEggs.toLocaleString()} eggs` },
                { label: 'Ordered Trays', value: `${order.quantityTrays} trays` },
                { label: 'Order Date', value: order.orderDate },
                { label: 'Required Date', value: order.requiredDate },
                { label: 'Assigned Store', value: order.assignedStore },
                { label: 'Order Value', value: `UGX ${order.totalValue.toLocaleString()}` },
              ].map(item => (
                <div key={item.label}>
                  <p className="text-xs text-gray-400">{item.label}</p>
                  <p className="text-xs font-semibold text-gray-800 mt-0.5">{item.value}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Stock Coverage */}
          <div className="bg-white border border-gray-100 rounded-xl p-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-bold text-gray-700 uppercase tracking-wide">Stock Coverage</p>
              <span className={`text-xs font-bold ${stockCoverage >= 100 ? 'text-green-600' : stockCoverage >= 50 ? 'text-amber-600' : 'text-red-500'}`}>
                {stockCoverage}%
              </span>
            </div>
            <div className="w-full bg-gray-100 rounded-full h-2 mb-3">
              <div
                className={`h-2 rounded-full transition-all ${stockCoverage >= 100 ? 'bg-green-500' : stockCoverage >= 50 ? 'bg-amber-400' : 'bg-red-400'}`}
                style={{ width: `${stockCoverage}%` }}
              />
            </div>
            <div className="grid grid-cols-3 gap-2 text-center">
              <div className="bg-blue-50 rounded-lg p-2">
                <p className="text-xs font-bold text-blue-700">{order.reservedEggs.toLocaleString()}</p>
                <p className="text-xs text-gray-400">Reserved</p>
              </div>
              <div className="bg-green-50 rounded-lg p-2">
                <p className="text-xs font-bold text-green-700">{order.availableStock.toLocaleString()}</p>
                <p className="text-xs text-gray-400">Available</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-2">
                <p className="text-xs font-bold text-gray-700">{Math.max(0, order.quantityEggs - order.reservedEggs).toLocaleString()}</p>
                <p className="text-xs text-gray-400">Shortfall</p>
              </div>
            </div>
          </div>

          {/* Notes */}
          {order.notes && (
            <div className="bg-amber-50 border border-amber-100 rounded-xl p-3">
              <p className="text-xs font-semibold text-amber-700 mb-1">Notes</p>
              <p className="text-xs text-amber-800 leading-relaxed">{order.notes}</p>
            </div>
          )}

          {/* Staff */}
          {(order.packedBy || order.dispatchedBy) && (
            <div className="bg-white border border-gray-100 rounded-xl p-4 space-y-2">
              <p className="text-xs font-bold text-gray-700 uppercase tracking-wide">Staff</p>
              {order.packedBy && <div className="flex justify-between text-xs"><span className="text-gray-400">Packed By</span><span className="font-semibold text-gray-800">{order.packedBy}</span></div>}
              {order.dispatchedBy && <div className="flex justify-between text-xs"><span className="text-gray-400">Dispatched By</span><span className="font-semibold text-gray-800">{order.dispatchedBy}</span></div>}
            </div>
          )}
        </div>

        {/* Action Footer */}
        {canAdvance && (
          <div className="p-5 border-t border-gray-100 sticky bottom-0 bg-white">
            <button
              onClick={() => { onAdvance(order.id); onClose(); }}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-green-600 text-white text-sm font-semibold rounded-xl hover:bg-green-700 transition-colors shadow-sm"
            >
              <Zap size={15} />
              {nextAction[order.status]}
              <ChevronRight size={15} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Ledger Entry Row ─────────────────────────────────────────────────────────

function LedgerMovementBadge({ type }: { type: EggStockMovement['movementType'] }) {
  const map: Record<EggStockMovement['movementType'], { label: string; bg: string; text: string }> = {
    production:    { label: 'Production',    bg: 'bg-green-100',  text: 'text-green-700' },
    transfer_in:   { label: 'Transfer In',   bg: 'bg-blue-100',   text: 'text-blue-700' },
    transfer_out:  { label: 'Transfer Out',  bg: 'bg-amber-100',  text: 'text-amber-700' },
    sale:          { label: 'Sale / Order',  bg: 'bg-indigo-100', text: 'text-indigo-700' },
    damage:        { label: 'Damage',        bg: 'bg-red-100',    text: 'text-red-600' },
    consumption:   { label: 'Consumption',   bg: 'bg-orange-100', text: 'text-orange-700' },
    adjustment:    { label: 'Adjustment',    bg: 'bg-gray-100',   text: 'text-gray-600' },
    return:        { label: 'Return',        bg: 'bg-teal-100',   text: 'text-teal-700' },
  };
  const cfg = map[type] ?? map.adjustment;
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${cfg.bg} ${cfg.text}`}>{cfg.label}</span>;
}

// ─── Main Component ───────────────────────────────────────────────────────────

const TABS = ['All Orders', 'Pending', 'Reserved', 'Packing', 'Dispatched', 'Delivered'];

export default function EggOrderFulfillmentContent() {
  const [activeTab, setActiveTab] = useState('All Orders');
  const [search, setSearch] = useState('');
  const [gradeFilter, setGradeFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [selectedOrder, setSelectedOrder] = useState<CustomerOrder | null>(null);
  const [orders, setOrders] = useState<CustomerOrder[]>(MOCK_ORDERS);
  const [reservations, setReservations] = useState<StockReservation[]>(MOCK_RESERVATIONS);
  const [inventory, setInventory] = useState<EggInventoryItem[]>(MOCK_EGG_INVENTORY);
  const [stockMovements, setStockMovements] = useState<EggStockMovement[]>(MOCK_STOCK_MOVEMENTS);
  const [toast, setToast] = useState<{ msg: string; type: 'success' | 'info' } | null>(null);
  const [showLedger, setShowLedger] = useState(false);
  const { can } = useRole();

  function showToast(msg: string, type: 'success' | 'info' = 'success') {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  }

  /**
   * Deducts reserved eggs from the inventory ledger when an order is dispatched or delivered.
   * Creates a traceable 'sale' stock movement record for each deduction.
   */
  function deductInventoryForOrder(order: CustomerOrder, newStatus: OrderStatus) {
    if (order.reservedEggs <= 0) return;

    const today = new Date().toISOString().slice(0, 10);
    const movType: EggStockMovement['movementType'] = 'sale';

    // Update inventory: find matching grade item and deduct from sold + closingStock
    setInventory(prev => prev.map(item => {
      const gradeMatch = item.grade.toLowerCase().includes(order.eggGrade.toLowerCase()) ||
        order.eggGrade.toLowerCase().includes(item.grade.toLowerCase());
      const storeMatch = item.store === order.assignedStore || true; // deduct from first matching grade
      if (gradeMatch) {
        const deductQty = Math.min(order.reservedEggs, item.closingStock);
        const deductTrays = Math.min(order.reservedTrays, item.trays);
        return {
          ...item,
          sold: item.sold + deductQty,
          closingStock: Math.max(0, item.closingStock - deductQty),
          trays: Math.max(0, item.trays - deductTrays),
        };
      }
      return item;
    }));

    // Create a new stock movement ledger entry
    const movId = `mov-${Date.now()}`;
    const movNumber = `MOV-2026-${String(stockMovements.length + 1).padStart(3, '0')}`;
    const newMovement: EggStockMovement = {
      id: movId,
      movementNumber: movNumber,
      date: today,
      movementType: movType,
      source: order.assignedStore,
      destination: order.customer,
      eggType: order.eggGrade,
      quantity: order.reservedEggs,
      trays: order.reservedTrays,
      looseEggs: 0,
      reference: order.orderNumber,
      createdBy: 'System',
      notes: `Reserved stock deducted — order ${newStatus === 'dispatched' ? 'dispatched' : 'delivered'} (${order.orderNumber})`,
    };
    setStockMovements(prev => [newMovement, ...prev]);

    // Mark reservation as fulfilled
    setReservations(prev => prev.map(r =>
      r.orderId === order.id ? { ...r, status: 'fulfilled' as const } : r
    ));
  }

  function advanceOrder(id: string) {
    const nextStatus: Partial<Record<OrderStatus, { status: OrderStatus; step: FulfillmentStep }>> = {
      pending:    { status: 'reserved',   step: 'reserved' },
      reserved:   { status: 'packing',    step: 'packing' },
      packing:    { status: 'dispatched', step: 'dispatch' },
      dispatched: { status: 'delivered',  step: 'delivered' },
    };

    setOrders(prev => prev.map(o => {
      if (o.id !== id) return o;
      const next = nextStatus[o.status];
      if (!next) return o;

      // Trigger inventory deduction when moving to Dispatched or Delivered
      if (next.status === 'dispatched' || next.status === 'delivered') {
        deductInventoryForOrder(o, next.status);
        const deductedMsg = o.reservedEggs > 0
          ? ` — ${o.reservedEggs.toLocaleString()} eggs deducted from inventory ledger`
          : '';
        showToast(
          `Order ${o.orderNumber} marked as ${STATUS_CONFIG[next.status].label}.${deductedMsg}`,
          'success'
        );
      } else {
        showToast(`Order ${o.orderNumber} advanced to ${STATUS_CONFIG[next.status].label}.`);
      }

      return { ...o, status: next.status, fulfillmentStep: next.step };
    }));
  }

  const filteredOrders = useMemo(() => {
    return orders.filter(o => {
      const tabMatch =
        activeTab === 'All Orders' ? true :
        activeTab === 'Pending'    ? o.status === 'pending' :
        activeTab === 'Reserved'   ? o.status === 'reserved' :
        activeTab === 'Packing'    ? o.status === 'packing' :
        activeTab === 'Dispatched' ? o.status === 'dispatched' :
        activeTab === 'Delivered'  ? o.status === 'delivered' : true;
      const searchMatch = !search || o.customer.toLowerCase().includes(search.toLowerCase()) || o.orderNumber.toLowerCase().includes(search.toLowerCase());
      const gradeMatch = !gradeFilter || o.eggGrade === gradeFilter;
      const priorityMatch = !priorityFilter || o.priority === priorityFilter;
      return tabMatch && searchMatch && gradeMatch && priorityMatch;
    });
  }, [orders, activeTab, search, gradeFilter, priorityFilter]);

  // KPI calculations
  const totalPending    = orders.filter(o => o.status === 'pending').length;
  const totalReserved   = orders.filter(o => o.status === 'reserved').length;
  const totalPacking    = orders.filter(o => o.status === 'packing').length;
  const totalDispatched = orders.filter(o => o.status === 'dispatched').length;
  const activeReservations = reservations.filter(r => r.status === 'active');
  const totalReservedEggs = activeReservations.reduce((s, r) => s + r.reservedQty, 0);
  const totalReservedTrays = activeReservations.reduce((s, r) => s + r.reservedTrays, 0);
  const urgentOrders = orders.filter(o => o.priority === 'high' && (o.status === 'pending' || o.status === 'reserved'));

  const grades = Array.from(new Set(orders.map(o => o.eggGrade)));

  // Ledger deductions (sale movements only, newest first)
  const ledgerDeductions = stockMovements.filter(m => m.movementType === 'sale');

  return (
    <div className="p-6">
      {/* Toast */}
      {toast && (
        <div className={`fixed top-4 right-4 z-50 flex items-center gap-3 text-white text-sm px-4 py-3 rounded-xl shadow-xl max-w-sm ${toast.type === 'success' ? 'bg-gray-900' : 'bg-blue-700'}`}>
          <span className={`w-2 h-2 rounded-full flex-shrink-0 ${toast.type === 'success' ? 'bg-green-400' : 'bg-blue-300'}`} />
          {toast.msg}
        </div>
      )}

      {/* Order Detail Drawer */}
      {selectedOrder && (
        <OrderDetailDrawer
          order={selectedOrder}
          onClose={() => setSelectedOrder(null)}
          onAdvance={advanceOrder}
        />
      )}

      {/* Page Header */}
      <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Order Fulfillment</h1>
          <p className="text-sm text-gray-500 mt-1">
            Link egg inventory to customer orders — track pending orders, reserved stock, and the full production-to-sale workflow.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => setShowLedger(v => !v)}
            className={`flex items-center gap-2 px-3 py-2 border text-sm font-medium rounded-xl transition-colors ${showLedger ? 'border-indigo-300 bg-indigo-50 text-indigo-700' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}
          >
            <BookOpen size={15} />
            Inventory Ledger
            {ledgerDeductions.length > 0 && (
              <span className="text-xs bg-indigo-100 text-indigo-700 px-1.5 py-0.5 rounded-full font-bold">{ledgerDeductions.length}</span>
            )}
          </button>
          <Link
            href="/egg-production/inventory"
            className="flex items-center gap-2 px-3 py-2 border border-gray-200 text-gray-600 text-sm font-medium rounded-xl hover:bg-gray-50 transition-colors"
          >
            <Package size={15} />
            View Inventory
          </Link>
          {can('create_invoices') && (
            <Link
              href="/sales"
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white text-sm font-semibold rounded-xl hover:bg-green-700 transition-colors shadow-sm"
            >
              <Plus size={15} />
              New Order
            </Link>
          )}
        </div>
      </div>

      {/* Urgent Alert */}
      {urgentOrders.length > 0 && (
        <div className="mb-5 flex items-center gap-3 bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-700">
          <AlertTriangle size={16} className="flex-shrink-0 text-red-500" />
          <span>
            <span className="font-semibold">{urgentOrders.length} high-priority order{urgentOrders.length > 1 ? 's' : ''}</span> require immediate action — check pending and reserved queues.
          </span>
        </div>
      )}

      {/* KPI Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
        {[
          { label: 'Pending Orders',    value: totalPending,    sub: 'Awaiting stock',       icon: Clock,         bg: 'bg-amber-100',  color: 'text-amber-600',  tab: 'Pending' },
          { label: 'Reserved Stock',    value: `${totalReservedEggs.toLocaleString()} eggs`, sub: `${totalReservedTrays} trays reserved`, icon: Package, bg: 'bg-blue-100', color: 'text-blue-600', tab: 'Reserved' },
          { label: 'Packing Now',       value: totalPacking,    sub: 'Being packed',         icon: ShoppingCart,  bg: 'bg-purple-100', color: 'text-purple-600', tab: 'Packing' },
          { label: 'Dispatched Today',  value: totalDispatched, sub: 'Out for delivery',     icon: Truck,         bg: 'bg-indigo-100', color: 'text-indigo-600', tab: 'Dispatched' },
        ].map(card => {
          const CardIcon = card.icon;
          return (
            <button
              key={card.label}
              onClick={() => setActiveTab(card.tab)}
              className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 flex items-start gap-3 hover:shadow-md transition-shadow text-left"
            >
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${card.bg}`}>
                <CardIcon size={18} className={card.color} />
              </div>
              <div>
                <p className="text-xs text-gray-500 font-medium">{card.label}</p>
                <p className="text-xl font-bold text-gray-900 mt-0.5">{card.value}</p>
                <p className="text-xs text-gray-400">{card.sub}</p>
              </div>
            </button>
          );
        })}
      </div>

      {/* ── Inventory Ledger Panel (collapsible) ── */}
      {showLedger && (
        <div className="bg-white rounded-xl border border-indigo-100 shadow-sm mb-6">
          <div className="p-4 border-b border-indigo-100 flex items-center justify-between">
            <div>
              <h2 className="text-base font-bold text-gray-900 flex items-center gap-2">
                <BookOpen size={16} className="text-indigo-500" />
                Inventory Ledger — Stock Deductions
              </h2>
              <p className="text-xs text-gray-400 mt-0.5">
                Automatic deductions recorded when orders are marked Dispatched or Delivered
              </p>
            </div>
            <span className="text-xs bg-indigo-100 text-indigo-700 px-2.5 py-1 rounded-full font-semibold">
              {ledgerDeductions.length} entries
            </span>
          </div>

          {/* Live inventory snapshot */}
          <div className="p-4 border-b border-gray-50 bg-gray-50/50">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Current Stock Snapshot</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {inventory.map(item => (
                <div key={item.id} className="bg-white rounded-lg border border-gray-100 p-3">
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-xs font-bold text-gray-800">{item.grade}</p>
                    <span className="text-xs font-bold text-green-700">{item.closingStock.toLocaleString()} eggs</span>
                  </div>
                  <p className="text-xs text-gray-400">{item.store} · {item.trays} trays</p>
                  <div className="mt-2 flex items-center gap-3 text-xs">
                    <span className="text-red-500">Sold: <span className="font-semibold">{item.sold.toLocaleString()}</span></span>
                    <span className="text-amber-500">Damaged: <span className="font-semibold">{item.damaged}</span></span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Movement log */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100">
                  {['Movement #', 'Date', 'Type', 'Egg Grade', 'From Store', 'To Customer', 'Qty Deducted', 'Trays', 'Order Ref', 'Recorded By', 'Notes'].map(h => (
                    <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {stockMovements.length === 0 ? (
                  <tr>
                    <td colSpan={11} className="px-4 py-8 text-center text-sm text-gray-400">
                      No stock movements recorded yet.
                    </td>
                  </tr>
                ) : stockMovements.map((mov, i) => (
                  <tr key={mov.id} className={`border-b border-gray-50 hover:bg-gray-50 ${i % 2 === 0 ? '' : 'bg-gray-50/30'}`}>
                    <td className="px-4 py-3 text-xs font-semibold text-indigo-700 whitespace-nowrap">{mov.movementNumber}</td>
                    <td className="px-4 py-3 text-xs text-gray-600 whitespace-nowrap">{mov.date}</td>
                    <td className="px-4 py-3"><LedgerMovementBadge type={mov.movementType} /></td>
                    <td className="px-4 py-3 text-xs text-gray-700 font-medium whitespace-nowrap">{mov.eggType}</td>
                    <td className="px-4 py-3 text-xs text-gray-600">{mov.source}</td>
                    <td className="px-4 py-3 text-xs text-gray-600">{mov.destination}</td>
                    <td className="px-4 py-3 text-xs font-bold text-red-600">-{mov.quantity.toLocaleString()}</td>
                    <td className="px-4 py-3 text-xs text-gray-600">{mov.trays}</td>
                    <td className="px-4 py-3 text-xs font-semibold text-green-700">{mov.reference ?? '—'}</td>
                    <td className="px-4 py-3 text-xs text-gray-500">{mov.createdBy}</td>
                    <td className="px-4 py-3 text-xs text-gray-400 max-w-xs truncate">{mov.notes ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="p-3 border-t border-gray-100 text-xs text-gray-400 text-right">
            Total deducted via orders: <span className="font-semibold text-gray-700">
              {ledgerDeductions.reduce((s, m) => s + m.quantity, 0).toLocaleString()} eggs
            </span>
          </div>
        </div>
      )}

      {/* Reserved Stock Panel */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm mb-6">
        <div className="p-4 border-b border-gray-100 flex items-center justify-between">
          <div>
            <h2 className="text-base font-bold text-gray-900">Active Stock Reservations</h2>
            <p className="text-xs text-gray-400 mt-0.5">Eggs currently locked against customer orders</p>
          </div>
          <span className="text-xs bg-blue-100 text-blue-700 px-2.5 py-1 rounded-full font-semibold">
            {activeReservations.length} Active
          </span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100">
                {['Order #', 'Egg Grade', 'Store', 'Reserved Qty', 'Reserved Trays', 'Reserved At', 'Expires', 'Status'].map(h => (
                  <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {reservations.map((res, i) => (
                <tr key={res.id} className={`border-b border-gray-50 hover:bg-gray-50 ${i % 2 === 0 ? '' : 'bg-gray-50/30'}`}>
                  <td className="px-4 py-3 text-xs font-semibold text-green-700">{res.orderNumber}</td>
                  <td className="px-4 py-3 text-xs text-gray-700 font-medium">{res.eggGrade}</td>
                  <td className="px-4 py-3 text-xs text-gray-600">{res.store}</td>
                  <td className="px-4 py-3 text-xs font-bold text-gray-900">{res.reservedQty.toLocaleString()}</td>
                  <td className="px-4 py-3 text-xs text-gray-600">{res.reservedTrays}</td>
                  <td className="px-4 py-3 text-xs text-gray-500">{res.reservedAt.slice(0, 10)}</td>
                  <td className="px-4 py-3 text-xs text-gray-500">{res.expiresAt.slice(0, 10)}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${
                      res.status === 'active'    ? 'bg-blue-100 text-blue-700' :
                      res.status === 'fulfilled' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                    }`}>
                      {res.status.charAt(0).toUpperCase() + res.status.slice(1)}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Orders Table */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm">
        <div className="p-4 border-b border-gray-100">
          <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
            <h2 className="text-base font-bold text-gray-900">Customer Orders</h2>
            <div className="flex items-center gap-2 flex-wrap">
              <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="Search orders..."
                  className="pl-8 pr-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 w-44"
                />
              </div>
              <select
                value={gradeFilter}
                onChange={e => setGradeFilter(e.target.value)}
                className="text-xs border border-gray-200 rounded-lg px-3 py-2 text-gray-600 bg-white focus:outline-none focus:ring-2 focus:ring-green-500"
              >
                <option value="">All Grades</option>
                {grades.map(g => <option key={g} value={g}>{g}</option>)}
              </select>
              <select
                value={priorityFilter}
                onChange={e => setPriorityFilter(e.target.value)}
                className="text-xs border border-gray-200 rounded-lg px-3 py-2 text-gray-600 bg-white focus:outline-none focus:ring-2 focus:ring-green-500"
              >
                <option value="">All Priorities</option>
                <option value="high">High</option>
                <option value="normal">Normal</option>
                <option value="low">Low</option>
              </select>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-1 bg-gray-100 p-1 rounded-xl w-fit overflow-x-auto">
            {TABS.map(tab => {
              const count = tab === 'All Orders' ? orders.length :
                orders.filter(o => o.status === tab.toLowerCase()).length;
              return (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all whitespace-nowrap ${
                    activeTab === tab ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {tab}
                  {count > 0 && (
                    <span className={`text-xs px-1.5 py-0.5 rounded-full font-bold ${
                      activeTab === tab ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-500'
                    }`}>{count}</span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100">
                {['Order #', 'Customer', 'Grade', 'Ordered', 'Reserved', 'Coverage', 'Required Date', 'Priority', 'Status', 'Workflow', 'Actions'].map(h => (
                  <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredOrders.length === 0 ? (
                <tr>
                  <td colSpan={11} className="px-4 py-12 text-center text-sm text-gray-400">
                    No orders match your filters.
                  </td>
                </tr>
              ) : filteredOrders.map((order, i) => {
                const coverage = order.quantityEggs > 0 ? Math.min(100, Math.round((order.reservedEggs / order.quantityEggs) * 100)) : 0;
                const isUrgent = order.priority === 'high' && (order.status === 'pending' || order.status === 'reserved');
                return (
                  <tr key={order.id} className={`border-b border-gray-50 hover:bg-gray-50 transition-colors ${i % 2 === 0 ? '' : 'bg-gray-50/30'} ${isUrgent ? 'border-l-2 border-l-red-400' : ''}`}>
                    <td className="px-4 py-3 text-xs font-semibold text-green-700 whitespace-nowrap">{order.orderNumber}</td>
                    <td className="px-4 py-3">
                      <div className="text-xs font-semibold text-gray-900">{order.customer}</div>
                      <div className="text-xs text-gray-400 capitalize">{order.customerType}</div>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-600 whitespace-nowrap">{order.eggGrade}</td>
                    <td className="px-4 py-3">
                      <div className="text-xs font-bold text-gray-900">{order.quantityEggs.toLocaleString()}</div>
                      <div className="text-xs text-gray-400">{order.quantityTrays} trays</div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-xs font-bold text-blue-700">{order.reservedEggs.toLocaleString()}</div>
                      <div className="text-xs text-gray-400">{order.reservedTrays} trays</div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-16 bg-gray-100 rounded-full h-1.5">
                          <div
                            className={`h-1.5 rounded-full ${coverage >= 100 ? 'bg-green-500' : coverage >= 50 ? 'bg-amber-400' : 'bg-red-400'}`}
                            style={{ width: `${coverage}%` }}
                          />
                        </div>
                        <span className={`text-xs font-semibold ${coverage >= 100 ? 'text-green-600' : coverage >= 50 ? 'text-amber-600' : 'text-red-500'}`}>
                          {coverage}%
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-600 whitespace-nowrap">{order.requiredDate}</td>
                    <td className="px-4 py-3"><PriorityDot priority={order.priority} /></td>
                    <td className="px-4 py-3"><StatusBadge status={order.status} /></td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        {WORKFLOW_STEPS.map((step, si) => {
                          const currentIdx = stepIndex(order.fulfillmentStep);
                          const done = si < currentIdx;
                          const active = si === currentIdx;
                          return (
                            <div
                              key={step.key}
                              title={step.label}
                              className={`w-2.5 h-2.5 rounded-full ${done ? 'bg-green-500' : active ? 'bg-green-600 ring-2 ring-green-200' : 'bg-gray-200'}`}
                            />
                          );
                        })}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => setSelectedOrder(order)}
                          className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
                          title="View Details"
                        >
                          <Eye size={13} />
                        </button>
                        {order.status !== 'delivered' && order.status !== 'cancelled' && (
                          <button
                            onClick={() => advanceOrder(order.id)}
                            className="p-1.5 rounded-lg hover:bg-green-50 text-gray-400 hover:text-green-600 transition-colors"
                            title="Advance Workflow"
                          >
                            <ArrowRight size={13} />
                          </button>
                        )}
                        {order.status === 'pending' && (
                          <button
                            onClick={() => showToast(`Order ${order.orderNumber} cancelled.`, 'info')}
                            className="p-1.5 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-500 transition-colors"
                            title="Cancel Order"
                          >
                            <RotateCcw size={13} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Footer summary */}
        <div className="p-4 border-t border-gray-100 flex items-center justify-between text-xs text-gray-400">
          <span>Showing {filteredOrders.length} of {orders.length} orders</span>
          <span>
            Total reserved: <span className="font-semibold text-gray-700">{totalReservedEggs.toLocaleString()} eggs ({totalReservedTrays} trays)</span>
          </span>
        </div>
      </div>
    </div>
  );
}
